import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import { server } from "../../server";
import { formatSubscriptionTierLabel } from '../../utils/subscriptionLabels';

const ShopSubscriptionCheckoutDirect = () => {
    const navigate = useNavigate();
    const { seller } = useSelector(state => state.seller);
    const { priceId } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [email, setEmail] = useState(seller?.email || '');
    const [shopName, setShopName] = useState(seller?.name || '');
    const [couponCode, setCouponCode] = useState('');
    const [priceDetails, setPriceDetails] = useState(null);
    const [discountedPrice, setDiscountedPrice] = useState(null);

    useEffect(() => {
        setEmail(seller?.email || '');
        setShopName(seller?.name || '');
    }, [seller?.email, seller?.name]);

    useEffect(() => {
        const fetchPriceDetails = async () => {
            try {
                const response = await axios.get(`${server}/subscriptions/price-details/${priceId}`);
                setPriceDetails(response.data);
            } catch (error) {
                console.error('Failed to fetch price details', error);
                toast.error('Failed to load pricing information.');
            }
        };

        fetchPriceDetails();
    }, [priceId]);

    // Function to apply the coupon code
    const handleApplyCoupon = async () => {
        if (!couponCode) {
            toast.error('Please enter a coupon code.');
            return;
        }

        try {
            const response = await axios.post(`${server}/subscriptioncouponcode/apply-subscription-coupon`, {
                code: couponCode,
                priceId
            });

            if (response.data.success) {
                toast.success('Coupon applied successfully!');
                setDiscountedPrice(response.data.finalPrice);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to apply coupon.');
        }
    };

    const handlePaymentSubmission = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        try {
            await axios.post(`${server}/subscriptions/shops/create-subscription`, {
                priceId,
                couponCode,
            }, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                    'current-profile': sessionStorage.getItem('currentProfile') || 'defaultProfile',
                }
            });

            toast.success('Subscription created successfully!');
            navigate('/');
        } catch (error) {
            console.error('Subscription creation failed:', error);
            toast.error('Failed to create subscription. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="subscription-checkout-layout shop-subscription-checkout-layout">
            <section className="subscription-checkout-card">
                <div className="subscription-checkout-card-header">
                    <p className="subscription-checkout-eyebrow">Shop subscription</p>
                    <h1>Confirm your plan</h1>
                </div>
                <form onSubmit={handlePaymentSubmission} className="subscription-checkout-form">
                    <div className="subscription-checkout-field">
                        <label className="subscription-checkout-label">Shop Name</label>
                        <input type="text" className="subscription-checkout-input" placeholder="Shop Name" value={shopName} readOnly required />
                    </div>

                    <div className="subscription-checkout-field">
                        <label className="subscription-checkout-label">Email Address</label>
                        <input type="email" className="subscription-checkout-input" placeholder="Email Address" value={email} readOnly required />
                    </div>

                    <div className="subscription-checkout-field">
                        <label className="subscription-checkout-label">Coupon Code</label>
                        <input type="text" className="subscription-checkout-input" placeholder="Coupon Code" value={couponCode} onChange={e => setCouponCode(e.target.value)} />
                    </div>

                    <div className="subscription-checkout-actions">
                        <button
                            type="button"
                            className="subscription-checkout-button subscription-checkout-button-secondary"
                            onClick={handleApplyCoupon}
                            disabled={isLoading}
                        >
                            Apply Coupon
                        </button>

                        <button type="submit" className="subscription-checkout-button subscription-checkout-button-primary" disabled={isLoading}>
                            {isLoading ? 'Processing...' : 'Confirm Subscription'}
                        </button>
                    </div>
                </form>
            </section>
            <aside className="subscription-checkout-summary">
                {priceDetails && (
                    <div className="subscription-details">
                        <h2>{`Upgrade your shop with the ${formatSubscriptionTierLabel(priceDetails.product.name)} plan`}</h2>
                        <p>{priceDetails.product.description}</p>
                        <p>Price: ${(discountedPrice !== null ? discountedPrice / 100 : priceDetails.unit_amount / 100).toFixed(2)} {priceDetails.currency.toUpperCase()}</p>
                    </div>
                )}
            </aside>
        </div>
    );
};

export default ShopSubscriptionCheckoutDirect;
